import React from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  Paper
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Key as KeyIcon,
  FingerprintOutlined
} from '@mui/icons-material';
import { User } from '../../types';
import { UserDetailSectionId, ZERDE_PRIMARY, ZERDE_EMERALD } from './types';

interface UserDetailSidebarProps {
  user: User;
  roleName: string;
  deactivation: number;
  avatarUrl: string | null;
  fullName: string;
  canEditUser: boolean;
  /** Якорь к форме пароля: users.edit_password в зоне оператора или своя карточка */
  canResetPassword: boolean;
  uploadingAvatar: boolean;
  handleAvatarUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  pos: number;
  reports: number;
  onScrollToSection: (sectionId: UserDetailSectionId) => void;
}

export const UserDetailSidebar: React.FC<UserDetailSidebarProps> = ({
  user,
  roleName,
  deactivation,
  avatarUrl,
  fullName,
  canEditUser,
  canResetPassword,
  uploadingAvatar,
  handleAvatarUpload,
  pos,
  reports,
  onScrollToSection
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Карточка Фото сотрудника с ленточкой роли */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
          position: 'relative',
          textAlign: 'center'
        }}
      >
        {/* Верхняя ленточка роли */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {/* Цвет бейджа задается в конструкторе ролей */}
          <Box
            sx={{
              bgcolor: user.roleBadge?.bg || ZERDE_PRIMARY,
              color: user.roleBadge?.text || '#ffffff',
              border: `1px solid ${user.roleBadge?.border || 'transparent'}`,
              fontSize: '0.7rem',
              fontWeight: 700,
              px: 1.4,
              py: 0.4,
              borderRadius: '6px',
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }}
          >
            {roleName}
          </Box>

          {/* Статус онлайн */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              fontSize: '0.78rem',
              fontWeight: 600,
              color: deactivation === 0 ? ZERDE_EMERALD : '#c2410c'
            }}
          >
            {deactivation === 0 ? 'Активен' : 'Деактивирован'}
          </Box>
        </Box>

        {/* Большой аватар 180x180px */}
        <Box sx={{ position: 'relative', display: 'inline-block', my: 1 }}>
          <Avatar
            src={avatarUrl || user.avatarUrl || undefined}
            sx={{
              width: 170,
              height: 170,
              border: '4px solid #f1f5f9',
              boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
              fontSize: '4rem',
              fontWeight: 700,
              bgcolor: ZERDE_PRIMARY
            }}
          >
            {!avatarUrl &&
              !user.avatarUrl &&
              (fullName
                ? fullName.charAt(0).toUpperCase()
                : user.username.charAt(0).toUpperCase())}
          </Avatar>
        </Box>

        {/* Кнопка смены фото */}
        {canEditUser && (
          <Box sx={{ mt: 1 }}>
            <Button
              component="label"
              size="small"
              disabled={uploadingAvatar}
              startIcon={<CameraIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                color: ZERDE_PRIMARY,
                fontSize: '0.82rem',
                fontWeight: 600,
                borderRadius: '6px',
                '&:hover': { bgcolor: 'rgba(21, 112, 239, 0.06)' }
              }}
            >
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleAvatarUpload}
              />
              {uploadingAvatar ? 'Сжатие 512x512...' : 'Сменить фото (512x512)'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Виджет «Статус сервисов» и быстрые якоря */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            display: 'block',
            mb: 1.8
          }}
        >
          Статус сервисов
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
              Кассовый доступ
            </Typography>
            <Chip
              label={pos === 1 ? 'Разрешен' : 'Заблокирован'}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.74rem',
                fontWeight: 600,
                bgcolor: pos === 1 ? '#ecfdf5' : '#fef2f2',
                color: pos === 1 ? '#059669' : '#dc2626',
                border: `1px solid ${pos === 1 ? '#a7f3d0' : '#fecaca'}`,
                borderRadius: '6px'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
              Модуль отчетов
            </Typography>
            <Chip
              label={reports === 1 ? 'Доступен' : 'Заблокирован'}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.74rem',
                fontWeight: 600,
                bgcolor: reports === 1 ? '#ecfdf5' : '#f8fafc',
                color: reports === 1 ? '#059669' : '#64748b',
                border: `1px solid ${reports === 1 ? '#a7f3d0' : '#e2e8f0'}`,
                borderRadius: '6px'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
              Почтовая сессия
            </Typography>
            <Chip
              label={user.hasMailSession ? 'Активна' : 'Не привязана'}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.74rem',
                fontWeight: 600,
                bgcolor: user.hasMailSession ? '#e0f2fe' : '#f8fafc',
                color: user.hasMailSession ? '#0284c7' : '#64748b',
                border: `1px solid ${user.hasMailSession ? '#bae6fd' : '#e2e8f0'}`,
                borderRadius: '6px'
              }}
            />
          </Box>
        </Box>

        {/* Быстрые кнопки-якоря */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2.5 }}>
          {canResetPassword && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<KeyIcon sx={{ fontSize: 18 }} />}
              onClick={() => onScrollToSection('security')}
              sx={{
                py: 0.9,
                borderRadius: '10px',
                borderColor: '#bfdbfe',
                color: ZERDE_PRIMARY,
                bgcolor: '#eff6ff',
                fontWeight: 600,
                fontSize: '0.84rem',
                textTransform: 'none',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: '#dbeafe',
                  borderColor: ZERDE_PRIMARY
                }
              }}
            >
              Сбросить пароль
            </Button>
          )}

          {/* Якорь биометрии, перенесенный сюда из блока Контактов */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FingerprintOutlined sx={{ fontSize: 18 }} />}
            onClick={() => onScrollToSection('biometrics')}
            sx={{
              py: 0.9,
              borderRadius: '10px',
              borderColor: '#e2e8f0',
              color: '#334155',
              bgcolor: '#f8fafc',
              fontWeight: 600,
              fontSize: '0.84rem',
              textTransform: 'none',
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: '#f1f5f9',
                borderColor: '#cbd5e1',
                color: ZERDE_PRIMARY
              }
            }}
          >
            Управление Face ID
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserDetailSidebar;
